import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import {
  EmptyStateComponent,
  InformationBoxComponent,
  StatusLabelComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  extendSortingDataAccessor,
  LoaderButtonComponent,
  NgCommonModule,
  ObservableDataSource,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { FundingRequest } from '@principle-theorem/principle-core';
import {
  ACC_CLAIM_COVER_STATUS_DISPLAY_MAP,
  AccClaimCoverStatus,
  FundingRequestStatus,
  IAccClaimFundingRequest,
  IBrand,
  IPatient,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_FORMAT,
  Firestore,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccApiService } from '../../../acc/acc-api.service';
import { AccClaimService } from '../../../acc/acc-claim.service';
import {
  AccViewClaimDialogComponent,
  IAccViewClaimDialogData,
} from '../acc-view-claim-dialog/acc-view-claim-dialog.component';
import { AccTestChipComponent } from '../acc-test-chip/acc-test-chip.component';

@Component({
  selector: 'pr-acc-claims-table',
  imports: [
    NgCommonModule,
    EmptyStateComponent,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    StatusLabelComponent,
    RouterModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    LoaderButtonComponent,
    MatProgressSpinnerModule,
    AccTestChipComponent,
    InformationBoxComponent,
  ],
  templateUrl: './acc-claims-table.component.html',
  styleUrls: ['./acc-claims-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AccClaimService, AccApiService],
})
export class AccClaimsTableComponent implements OnDestroy {
  private _dialog = inject(MatDialog);
  private _router = inject(Router);
  accApi = inject(AccApiService);
  accClaimService = inject(AccClaimService);

  practice = input.required<WithRef<IPractice>>();
  brand = input.required<WithRef<IBrand>>();
  patient = input<WithRef<IPatient> | undefined>();
  dataSource: ObservableDataSource<WithRef<IAccClaimFundingRequest>>;
  noClaims$: Observable<boolean>;
  claimRequests$: Observable<WithRef<IAccClaimFundingRequest>[]>;
  displayedColumns = computed(() =>
    compact([
      'status',
      'createdAt',
      'claimNumber',
      'declarationDate',
      !this.patient() ? 'patient' : undefined,
      'coverStatus',
      'syncStatus',
      'actions',
    ])
  );

  displayAddressVerificationWarning = computed(
    () =>
      !this.practice().metadata?.address?.isVerified &&
      !this.accApi.testModeEnabled()
  );

  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly pageSizeOptions = [25, 50, 100];
  readonly pageSize = 25;

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  constructor() {
    this.claimRequests$ = this.accClaimService.getAccClaimRequests$(
      toObservable(this.patient)
    );
    this.dataSource = new ObservableDataSource(this.claimRequests$);
    this.noClaims$ = this.dataSource.filteredLength$.pipe(
      map((length) => length === 0)
    );
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
  }

  getStatusColour(status: FundingRequestStatus): string {
    return FundingRequest.getStatusColour(status);
  }

  getCoverStatusLabel(status?: AccClaimCoverStatus): string {
    if (!status) {
      return 'N/A';
    }

    return ACC_CLAIM_COVER_STATUS_DISPLAY_MAP[status] ?? 'N/A';
  }

  patientLink(patient: INamedDocument<IPatient>): string[] {
    const brand = this.brand();
    return ['/', brand.slug, 'patients', patient.ref.id];
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  async lodgeClaim(patient: WithRef<IPatient>): Promise<void> {
    await this.accClaimService.viewClaimLodgement(patient);
  }

  async viewClaimLodgement(
    claim: WithRef<IAccClaimFundingRequest>
  ): Promise<void> {
    const patient = await Firestore.getDoc(claim.patient.ref);
    await this.accClaimService.viewClaimLodgement(patient, claim);
  }

  async verifyPracticeAddress(): Promise<void> {
    const practice = this.practice();
    const url = ['/', 'settings', 'practice', practice.ref.id, 'general'];
    await this._router.navigate(url);
  }

  viewClaimDetails(claimRequest: WithRef<IAccClaimFundingRequest>): void {
    this._dialog.open<AccViewClaimDialogComponent, IAccViewClaimDialogData>(
      AccViewClaimDialogComponent,
      DialogPresets.medium({ data: { claimRequest } })
    );
  }

  deleteDisabled(claimRequest: WithRef<IAccClaimFundingRequest>): boolean {
    return claimRequest.status !== FundingRequestStatus.Draft;
  }

  private _sortingDataAccessor(
    data: WithRef<IAccClaimFundingRequest>,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      case 'type':
        return data.type;
      case 'status':
        return data.status;
      default:
        return;
    }
  }
}
